import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { customersApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { useModuleScopedBreadcrumbs } from '@/hooks/useModuleScopedBreadcrumbs';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { CustomerRequest, CustomerResponse, WalletTransactionType } from '@/types/customer';
import { formatCurrency } from '@/utils/format';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const empty: CustomerRequest = { name: '' };

export function CustomersPage() {
  const breadcrumbs = useModuleScopedBreadcrumbs('Customers');
  const currency = useAppSelector((s) => s.app.selectedStore?.currencyCode ?? 'INR');
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<CustomerResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletCustomer, setWalletCustomer] = useState<CustomerResponse | null>(null);
  const [walletAmount, setWalletAmount] = useState('');
  const [walletType, setWalletType] = useState<WalletTransactionType>('CREDIT');
  const [walletNotes, setWalletNotes] = useState('');
  const [editing, setEditing] = useState<CustomerResponse | null>(null);
  const [form, setForm] = useState<CustomerRequest>(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customersApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<CustomerResponse>[] = [
    { key: 'name', header: 'Name' },
    { key: 'phone', header: 'Phone' },
    { key: 'email', header: 'Email' },
    { key: 'loyaltyPoints', header: 'Points' },
    {
      key: 'walletBalance',
      header: 'Wallet',
      render: (r) => formatCurrency(r.walletBalance, currency),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex gap-2">
          <button
            type="button"
            className="text-primary-600"
            onClick={() => {
              setEditing(r);
              setForm({ name: r.name, phone: r.phone, email: r.email, address: r.address });
              setModalOpen(true);
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="text-slate-600"
            onClick={() => {
              setWalletCustomer(r);
              setWalletAmount('');
              setWalletType('CREDIT');
              setWalletNotes('');
              setWalletOpen(true);
            }}
          >
            Wallet
          </button>
        </div>
      ),
    },
  ];

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await customersApi.update(editing.id, form);
      else await customersApi.create(form);
      notify.success('Saved');
      setModalOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveWallet = async () => {
    if (!walletCustomer) return;
    const amount = Number(walletAmount);
    if (!amount || amount <= 0) {
      notify.error('Enter a positive amount');
      return;
    }
    setSaving(true);
    try {
      await customersApi.walletTransaction(walletCustomer.id, {
        amount,
        transactionType: walletType,
        notes: walletNotes || undefined,
      });
      notify.success(walletType === 'CREDIT' ? 'Wallet topped up' : 'Wallet debited');
      setWalletOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Wallet update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Customers"
        description="Manage customer records and wallet balances"
        breadcrumbs={breadcrumbs}
        actions={
          <Button onClick={() => { setEditing(null); setForm(empty); setModalOpen(true); }}>
            <AddIcon fontSize="small" /> Add customer
          </Button>
        }
      />
      <DataTable columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => { pagination.setSearch(v); pagination.resetPage(); }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
        emptyTitle="No customers yet"
        emptyDescription="Add your first customer to track sales, loyalty, and wallet balance."
        emptyActionLabel="Add customer"
        onEmptyAction={() => {
          setEditing(null);
          setForm(empty);
          setModalOpen(true);
        }}
      />
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit customer' : 'New customer'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} loading={saving}>Save</Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Phone" value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input label="Email" value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Address" value={form.address ?? ''} onChange={(e) => setForm({ ...form, address: e.target.value })} className="sm:col-span-2" />
        </div>
      </Modal>
      <Modal
        open={walletOpen}
        onClose={() => setWalletOpen(false)}
        title={walletCustomer ? `Wallet — ${walletCustomer.name}` : 'Wallet'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setWalletOpen(false)}>Cancel</Button>
            <Button onClick={saveWallet} loading={saving}>
              <AccountBalanceWalletIcon fontSize="small" /> Apply
            </Button>
          </>
        }
      >
        {walletCustomer && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Current balance:{' '}
              <strong>{formatCurrency(walletCustomer.walletBalance, currency)}</strong>
            </p>
            <Select
              label="Transaction"
              value={walletType}
              onChange={(v) => setWalletType(v as WalletTransactionType)}
              options={[
                { value: 'CREDIT', label: 'Credit (top-up)' },
                { value: 'DEBIT', label: 'Debit (adjustment)' },
              ]}
            />
            <Input
              label={`Amount (${currency})`}
              type="number"
              min={0}
              step="0.01"
              value={walletAmount}
              onChange={(e) => setWalletAmount(e.target.value)}
            />
            <Input
              label="Notes"
              value={walletNotes}
              onChange={(e) => setWalletNotes(e.target.value)}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
